import { useState } from 'react';

const demos = [
  {
    icon: 'ri-tools-line',
    title: 'HVAC AI Demo',
    description: '24/7 call answering, booking & lead capture for HVAC businesses',
    subtext: 'Missed calls → booked jobs automatically',
    url: 'https://hvac.go247ai.com/',
    glowColor: 'rgba(0, 194, 255, 0.15)',
    borderGlow: 'rgba(0, 194, 255, 0.4)',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Legal AI Demo',
    description: 'Law firm intake, qualification, and consultation booking system',
    subtext: 'Inbound leads → retained clients',
    url: 'https://law.aiconnectdemo.com/offer-5108-8460',
    glowColor: 'rgba(0, 194, 255, 0.15)',
    borderGlow: 'rgba(0, 194, 255, 0.4)',
  },
  {
    icon: 'ri-filter-3-line',
    title: 'Lead Capture Flow',
    description: 'Universal AI qualification + appointment funnel system',
    subtext: 'Traffic → qualified appointments',
    url: 'https://apply.aiconnectdemo.com/welcome599967',
    glowColor: 'rgba(0, 194, 255, 0.15)',
    borderGlow: 'rgba(0, 194, 255, 0.4)',
  },
];

const proofItems = [
  { label: 'Live deployments' },
  { label: '24/7 automation active' },
  { label: 'Revenue-generating systems' },
];

function DemoCard({ demo }: { demo: typeof demos[0] }) {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={demo.url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group block cursor-pointer no-underline"
      style={{
        transform: hovered ? 'translateY(-6px) scale(1.015)' : 'translateY(0) scale(1)',
        transition: 'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
        borderRadius: '16px',
        background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
        backdropFilter: 'blur(12px)',
        border: hovered ? `1px solid ${demo.borderGlow}` : '1px solid rgba(255,255,255,0.08)',
        boxShadow: hovered
          ? `0 0 32px ${demo.glowColor}, 0 20px 48px rgba(0,0,0,0.4)`
          : '0 2px 16px rgba(0,0,0,0.3)',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
      }}
    >
      {/* Icon */}
      <div
        className="w-12 h-12 flex items-center justify-center mb-5 rounded-xl"
        style={{
          background: hovered
            ? 'linear-gradient(135deg, #00C2FF 0%, #0077FF 100%)'
            : 'linear-gradient(135deg, rgba(0,194,255,0.2) 0%, rgba(0,119,255,0.15) 100%)',
          boxShadow: hovered ? '0 0 20px rgba(0,194,255,0.5)' : 'none',
          transition: 'background 0.28s ease, box-shadow 0.28s ease',
        }}
      >
        <i
          className={`${demo.icon} text-xl`}
          style={{
            color: hovered ? '#fff' : '#00C2FF',
            transition: 'color 0.28s ease',
          }}
        ></i>
      </div>

      {/* Title */}
      <h3
        className="text-xl font-bold mb-2 whitespace-nowrap"
        style={{
          color: hovered ? '#00C2FF' : '#F0F4FF',
          transition: 'color 0.28s ease',
          fontFamily: "'Inter', sans-serif",
          letterSpacing: '-0.02em',
        }}
      >
        {demo.title}
      </h3>

      {/* Description */}
      <p
        className="text-sm mb-5 leading-relaxed"
        style={{ color: 'rgba(180,195,220,0.8)', lineHeight: '1.6' }}
      >
        {demo.description}
      </p>

      {/* Divider */}
      <div
        className="mb-4"
        style={{
          height: '1px',
          background: hovered
            ? 'linear-gradient(90deg, transparent, rgba(0,194,255,0.3), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
          transition: 'background 0.28s ease',
        }}
      />

      {/* Subtext */}
      <p
        className="text-xs mb-5 font-medium tracking-wide"
        style={{ color: 'rgba(0,194,255,0.7)', fontStyle: 'italic' }}
      >
        {demo.subtext}
      </p>

      {/* CTA */}
      <div className="flex items-center gap-2 mt-auto">
        <span
          className="text-sm font-semibold"
          style={{
            color: hovered ? '#00C2FF' : 'rgba(255,255,255,0.6)',
            transition: 'color 0.28s ease',
          }}
        >
          Visit Demo
        </span>
        <i
          className="ri-arrow-right-line text-sm"
          style={{
            color: hovered ? '#00C2FF' : 'rgba(255,255,255,0.4)',
            transform: hovered ? 'translateX(4px)' : 'translateX(0)',
            transition: 'color 0.28s ease, transform 0.28s ease',
          }}
        ></i>
      </div>
    </a>
  );
}

export default function LiveDemos() {
  return (
    <section
      id="live-demos"
      style={{
        background: 'linear-gradient(180deg, #060E1E 0%, #0A1628 40%, #060E1E 100%)',
        padding: '100px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '300px',
          background: 'radial-gradient(ellipse, rgba(0,194,255,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="max-w-6xl mx-auto relative">

        {/* Header */}
        <div className="text-center mb-6">
          <div
            className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full text-xs font-semibold tracking-widest uppercase"
            style={{
              background: 'rgba(0,194,255,0.08)',
              border: '1px solid rgba(0,194,255,0.2)',
              color: '#00C2FF',
              letterSpacing: '0.12em',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#00C2FF',
                display: 'inline-block',
                boxShadow: '0 0 8px #00C2FF',
                animation: 'pulse 2s ease-in-out infinite',
              }}
            />
            Active Deployments
          </div>

          <h2
            className="font-bold mb-5"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: '1.15',
              letterSpacing: '-0.03em',
              color: '#F0F4FF',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Experience the System{' '}
            <span
              style={{
                background: 'linear-gradient(90deg, #00C2FF 0%, #0077FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Live
            </span>
          </h2>

          <p
            className="max-w-xl mx-auto"
            style={{
              fontSize: '1.05rem',
              color: 'rgba(180,195,220,0.75)',
              lineHeight: '1.7',
            }}
          >
            These are active deployments — not mockups.
          </p>
        </div>

        {/* Proof Bar */}
        <div className="flex items-center justify-center gap-6 mb-16 flex-wrap">
          {proofItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className="w-5 h-5 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(0,194,255,0.12)' }}
              >
                <i className="ri-check-line text-xs" style={{ color: '#00C2FF' }}></i>
              </div>
              <span
                className="text-sm font-medium"
                style={{ color: 'rgba(180,195,220,0.7)' }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Demo Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {demos.map((demo, i) => (
            <DemoCard key={i} demo={demo} />
          ))}
        </div>

        {/* Trust line */}
        <div className="text-center">
          <div
            className="inline-block px-8 py-4 rounded-xl"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <p
              className="text-sm font-medium"
              style={{ color: 'rgba(180,195,220,0.65)', letterSpacing: '0.02em' }}
            >
              Fully operational infrastructure built and refined over the past 6 months.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </section>
  );
}
